import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { DataTable } from '@unified-checkout/design-system';

import { useLangStore } from '@app/dashboard/store/lang';
import { useScreenWidth } from '@app/dashboard/hooks/useScreenWidth';
import { useTestLiveModeStore } from '@app/dashboard/store/testLiveMode';
import type { IColumnsMap } from 'libs/design-system/src/types/data_table';
import { INNER_ROUTES } from '@app/dashboard/constants/inner-routes';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { MOBILE_BREAKPOINT } from '@app/dashboard/constants/break-points';
import { getChargeBacks } from '@app/dashboard/services/inner_layout/chargebacks';

import Header from '../ChargeBacks/Header';

import styles from './ChargeBacks.module.scss';

const ChargeBacks = () => {
  const { t } = useTranslation();
  const screenWidth = useScreenWidth();
  const navigate = useNavigate();

  const { lang } = useLangStore();
  const { isLive } = useTestLiveModeStore();

  const isMobile = screenWidth <= MOBILE_BREAKPOINT;

  const columnsMap: IColumnsMap = [
    {
      id: 'id',
      name: t('innerLayout.chargeBacks.table.headers.id'),
      type: 'text',
      key: 'id',
    },
    {
      id: 'orderId',
      name: t('innerLayout.chargeBacks.table.headers.orderId'),
      type: 'text',
      key: 'order.id',
    },
    {
      id: 'amount_cents',
      name: t('innerLayout.chargeBacks.table.headers.amount'),
      type: 'amount_cents',
      key: 'amount_cents',
      additionalText: {
        key: 'currency',
        placement: lang === 'en' ? 'start' : 'end',
      },
    },
    {
      id: 'chargeback_status',
      name: t('innerLayout.chargeBacks.table.headers.status'),
      type: 'badge',
      key: 'chargeback_status',
      labelComparingValue: 'chargeback_status',
      label: {
        Open: {
          text: t('innerLayout.chargeBacks.status.open'),
          color: 'yellow',
        },
        'Merchant Contested': {
          text: t('innerLayout.chargeBacks.status.merchantContested'),
          color: 'gray',
        },
        'Merchant Accepted': {
          text: t('innerLayout.chargeBacks.status.merchantAccepted'),
          color: 'primary',
        },
        'Merchant Lost': {
          text: t('innerLayout.chargeBacks.status.merchantLost'),
          color: 'red',
        },
        'Merchant Won': {
          text: t('innerLayout.chargeBacks.status.merchantWon'),
          color: 'green',
        },
      },
      labelFallback: {
        text: t('innerLayout.chargeBacks.status.open'),
        color: 'yellow',
      },
    },
    {
      id: 'created_at',
      name: t('innerLayout.chargeBacks.table.headers.chargeDate'),
      type: 'date',
      key: 'created_at',
    },
  ];

  const handleTableRowClick = (rowObj: any) => {
    rowObj &&
      navigate(INNER_ROUTES.SINGLE_CHARGE_BACK.replace(':id', rowObj.id));
  };

  const queryKeys = useMemo(
    () => [QUERY_KEYS.GET_CHARGE_BACKS, isLive, lang],
    [isLive, lang]
  );

  return (
    <div className={styles['chargeBacks-wrapper']}>
      <Header />
      <DataTable
        lang={lang}
        tableBody={{
          lang: lang,
          dataLocation: 'data.results',
          columnsMap: columnsMap,
          rowClickHandler: handleTableRowClick,
        }}
        pagination={{
          hideLabels: isMobile,
          type: 'cursor_based',
          dataLocation: {
            next: 'data.next',
            prev: 'data.previous',
          },
          isMobile: isMobile,
        }}
        queries={{
          queryKeys: queryKeys,
          queryFunction: getChargeBacks,
        }}
        emptyState={{
          text: t('innerLayout.chargeBacks.table.body.noChargeBacksYet'),
          description: t(
            'innerLayout.chargeBacks.table.body.noChargeBacksHint'
          ),
        }}
      />
    </div>
  );
};

export default ChargeBacks;
